import { CloseOutlined, DownOutlined, MenuOutlined } from "@ant-design/icons";
import { Dropdown, Space } from "antd";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CiGlobe } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { Logo } from "../../assets/index";
import { logout } from "../../redux/slices/authSlice";

/**
 * A component that renders the header of the application.
 * It has different views depending on whether the user is logged in or not.
 * On smaller screen, it regresses into a simpler header with the rest of the elements becoming a sidebar
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */

const Header = () => {
  const [t, i18n] = useTranslation("global");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const user = useSelector((state) => state.authData.user);
  const isLoggedIn = user ? true : false;
  const languageText = i18n.language === "en" ? "ar" : "en";
  const changeLanguage = () => {
    i18n.changeLanguage(languageText);
    if (window.location.pathname === "/payment-confirmation") {
      window.location.reload();
    }
  };
  const dispatch = useDispatch();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const mobileNavigationLinks = [
    {
      id: "home",
      text: t("header.menu.home_text"),
      to: "/",
      hidden: false,
    },
    {
      id: "services",
      text: t("header.menu.services_text"),
      to: "/#services",
      hidden: location.pathname !== "/",
    },
    {
      id: "vehicleType",
      text: t("header.menu.vehicle_text"),
      to: "/#vehicleType",
      hidden: location.pathname !== "/",
    },
    { id: "aboutUs", text: t("about_us_text"), to: "/about-us", hidden: false },
    {
      id: "joinAgent",
      text: t("join_agent_text"),
      to: "/join-agent",
      hidden: user?.hasOwnProperty("agent"),
    },
  ];

  const navigationLinks = [
    { id: "home", text: t("header.menu.home_text"), to: "/", hidden: false },
    {
      id: "services",
      text: t("header.menu.services_text"),
      to: "/#services",
      hidden: false,
    },
    {
      id: "vehicleType",
      text: t("header.menu.vehicle_text"),
      to: "/#vehicleType",
      hidden: false,
    },
    { id: "aboutUs", text: t("about_us_text"), to: "/about-us", hidden: false },
    {
      id: "joinAgent",
      text: t("join_agent_text"),
      to: "/join-agent",
      hidden: user?.hasOwnProperty("agent"),
    },
  ];

  const settingsDropdownItms = user?.hasOwnProperty("agent")
    ? [
        {
          label: (
            <NavLink to="/my-bookings">
              {t("header.user_dropdown.agent_booking")}
            </NavLink>
          ),
          key: "1",
        },
        {
          label: (
            <button
              onClick={() => {
                dispatch(logout());
              }}
              style={{ width: "100%", textAlign: "center", display: "flex" }}
            >
              {t("header.user_dropdown.logout")}
            </button>
          ),
          key: "2",
        },
      ]
    : [
        {
          label: (
            <NavLink to="/my-profile">
              {t("header.user_dropdown.my_profile")}
            </NavLink>
          ),
          key: "1",
        },
        {
          label: (
            <NavLink to="/my-bookings">
              {t("header.user_dropdown.my_booking")}
            </NavLink>
          ),
          key: "2",
        },
        {
          label: (
            <button
              onClick={() => {
                dispatch(logout());
              }}
              style={{ width: "100%", textAlign: "center", display: "flex" }}
            >
              {t("header.user_dropdown.logout")}
            </button>
          ),
          key: "3",
        },
      ];
  const DropDownPart = user?.hasOwnProperty("agent") ? (
    <>
      <Dropdown
        menu={{
          items: settingsDropdownItms,
        }}
      >
        <button
          className="cursor-pointer lg:text-text_white text-background_steel_blue lg:bg-background_steel_blue bg-background_white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg sm:text-sm text-xs sm:px-5 px-1 py-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
          onClick={(e) => e.preventDefault()}
        >
          <Space>
            <p className="truncate max-w-[60px] sm:max-w-max">
              {user?.username}
            </p>
            <DownOutlined />
          </Space>
        </button>
      </Dropdown>
      <div className="flex items-center">
        <NavLink
          to="/create-new-account"
          className="cursor-pointer text-text_white bg-background_steel_blue border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg sm:text-sm text-xs sm:px-5 px-1 py-2.5 me-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
          type="button"
        >
          {t("header.create_text")}
        </NavLink>
      </div>
    </>
  ) : (
    <Dropdown
      menu={{
        items: settingsDropdownItms,
      }}
    >
      <button
        className="cursor-pointer lg:text-text_white text-background_steel_blue lg:bg-background_steel_blue bg-background_white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
        onClick={(e) => e.preventDefault()}
      >
        <Space>
          {user?.username}
          <DownOutlined />
        </Space>
      </button>
    </Dropdown>
  );

  return (
    <div className={"h-[80px]"}>
      <div
        id="drawer-navigation"
        className={clsx(
          "invisible fixed shadow-[0px_0px_8px_#00000045] bg-background_white z-50 text-background_steel_blue ltr:-translate-x-full rtl:translate-x-full top-0 ltr:left-0 rtl:right-0 w-64 h-screen lg:hidden px-4 overflow-hidden transition-all",
          {
            "!visible !translate-x-0": isSidebarOpen,
          }
        )}
        tabIndex="-1"
        aria-labelledby="drawer-navigation-label"
      >
        <button
          type="button"
          onClick={toggleSidebar}
          aria-controls="drawer-navigation"
          className="text-text_white bg-background_steel_blue hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 absolute top-2.5 end-2.5 inline-flex items-center"
        >
          <CloseOutlined />
          <span className="sr-only">Close menu</span>
        </button>
        <ul className="lg:hidden gap-4 flex flex-col mt-8 z-50">
          {mobileNavigationLinks.map(
            (link) =>
              !link.hidden && (
                <li key={link.id}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      isActive &&
                      (location.hash === `#${link.id}` ||
                        (!link.to.includes("#") && !location.hash))
                        ? "relative flex w-full pb-3 befor:conent-[''] before:border-b-2 before:bg-background_steel_blue before:absolute before:bottom-0 before:left-0 before:w-full before:h-[3px]"
                        : "relative flex w-full"
                    }
                  >
                    {link.text}
                  </NavLink>
                </li>
              )
          )}
          <li>
            <button
              type="button"
              onClick={changeLanguage}
              className="w-full text-text_black uppercase flex flex-row justify-center items-baseline bg-background_darkgrey font-medium text-md py-1"
            >
              <div className="text-md px-2">{languageText}</div>
            </button>
          </li>
        </ul>
      </div>

      <header
        className={clsx(
          "bg-text_white z-40 fixed top-0 w-full h-20 py-2.5 shadow-md flex flex-wrap justify-between text-[#E9B838] items-center sm:px-4 px-1 lg:px-12"
        )}
      >
        <div className="flex items-center gap-2 justify-center ml-2 mr-2">
          <div className="flex items-center lg:hidden">
            <button
              onClick={toggleSidebar}
              className="text-text_lightdark_grey"
              type="button"
            >
              <MenuOutlined />
            </button>
          </div>
          <NavLink to="/">
            <img src={Logo} alt="camelo logo" className="sm:w-12 w-8 h-auto" />
            {/* <Logo /> */}
          </NavLink>
        </div>
        <ul
          className={clsx(
            "lg:flex h-full justify-center items-center sm:gap-8 gap-0 hidden"
          )}
        >
          {navigationLinks.map(
            (link) =>
              !link.hidden && (
                <li
                  key={link.id}
                  className="relative h-full flex items-center justify-center"
                >
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      isActive &&
                      (location.hash === `#${link.id}` ||
                        (!link.to.includes("#") && !location.hash))
                        ? "befor:conent-[''] before:border-b-2 before:bg-background_steel_blue before:absolute before:bottom-[-10px] before:left-0 before:w-full before:h-1"
                        : ""
                    }
                  >
                    {link.text}
                  </NavLink>
                </li>
              )
          )}
        </ul>
        <div className={clsx("flex gap-1 sm:gap-2 justify-end")}>
          <button
            type="button"
            className="flex items-center justify-center capitalize"
            onClick={changeLanguage}
          >
            <p className="mr-2">{languageText}</p>
            <CiGlobe className="sm:text-2xl text-base" />
          </button>
          {!isLoggedIn ? (
            <>
              <div>
                <NavLink
                  to="/sign-in"
                  className="bg-transparent text-[#E9B838] font-medium sm:text-md sm:px-2.5 px-1 cursor-pointer"
                  label={t("header.sign_in_text")}
                  type="button"
                >
                  {t("header.sign_in_text")}
                </NavLink>
              </div>
              <div>
                <NavLink
                  to="/create-new-account"
                  className="cursor-pointer text-text_white bg-background_steel_blue border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg sm:text-sm text-xs sm:px-5 px-1 py-2.5 me-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
                  type="button"
                >
                  {t("header.create_text")}
                </NavLink>
              </div>
            </>
          ) : (
            DropDownPart
          )}
        </div>
      </header>
      {isSidebarOpen && (
        <button
          onClick={toggleSidebar}
          className="fixed top-0 left-0 right-0 bottom-0 h-full w-full z-40 bg-text_black/30"
        ></button>
      )}
    </div>
  );
};

export default Header;
